import { CheckIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';
import { normalizeEmail, normalizeOptionalText, normalizePhone } from '@sixam/shared/contact-core';

export type ContactCaptureValues = Readonly<{
  name: string;
  email: string;
  phone: string;
  marketingConsent: boolean;
}>;

export function ContactCaptureForm({
  defaultEmail,
  defaultName,
  defaultPhone,
  error,
  isSubmitting,
  onSubmit,
  submitLabel = 'Save contact',
}: Readonly<{
  defaultEmail?: string | null;
  defaultName?: string | null;
  defaultPhone?: string | null;
  error?: string | null;
  isSubmitting?: boolean;
  onSubmit: (values: ContactCaptureValues) => void;
  submitLabel?: string;
}>) {
  const [name, setName] = React.useState(defaultName ?? '');
  const [email, setEmail] = React.useState(defaultEmail ?? '');
  const [phone, setPhone] = React.useState(defaultPhone ?? '');
  const [marketingConsent, setMarketingConsent] = React.useState(true);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  function handleSubmit() {
    if (!normalizeOptionalText(name)) {
      setValidationError('Add your name before continuing.');
      return;
    }
    if (!normalizeEmail(email)) {
      setValidationError('Add a valid email before continuing.');
      return;
    }
    if (!normalizePhone(phone)) {
      setValidationError('Add a phone number before continuing.');
      return;
    }

    setValidationError(null);
    onSubmit({ email, marketingConsent, name, phone });
  }

  return (
    <View className="gap-5">
      <View className="gap-2">
        <Text variant="label" className="text-muted-foreground">
          Contact
        </Text>
        <Text variant="h3">Run-day details</Text>
        <Text variant="muted">
          Keep your registration reachable for captain updates and check-in support.
        </Text>
      </View>

      <View className="gap-4">
        <View className="gap-1.5">
          <Label>Name</Label>
          <Input
            autoComplete="name"
            editable={!isSubmitting}
            textContentType="name"
            value={name}
            onChangeText={setName}
            placeholder="Mai Arun"
          />
        </View>
        <View className="gap-1.5">
          <Label>Email</Label>
          <Input
            autoCapitalize="none"
            autoComplete="email"
            editable={!isSubmitting}
            keyboardType="email-address"
            textContentType="emailAddress"
            value={email}
            onChangeText={setEmail}
            placeholder="runner@6ambkk.com"
          />
        </View>
        <View className="gap-1.5">
          <Label>Phone</Label>
          <Input
            autoComplete="tel"
            editable={!isSubmitting}
            keyboardType="phone-pad"
            textContentType="telephoneNumber"
            value={phone}
            onChangeText={setPhone}
            placeholder="+66 81 234 5678"
          />
        </View>
      </View>

      <Pressable
        accessibilityRole="checkbox"
        accessibilityState={{ checked: marketingConsent, disabled: Boolean(isSubmitting) }}
        className="flex-row items-start gap-3 rounded-xl border border-border bg-card p-4 active:opacity-80"
        disabled={isSubmitting}
        onPress={() => setMarketingConsent((current) => !current)}
      >
        <View
          className="mt-0.5 h-5 w-5 items-center justify-center rounded border border-primary"
          style={{ backgroundColor: marketingConsent ? BRAND.color.orange : 'transparent' }}
        >
          {marketingConsent ? <CheckIcon color="white" size={14} /> : null}
        </View>
        <View className="min-w-0 flex-1 gap-1">
          <Text className="font-sans text-xs font-bold uppercase tracking-widest">
            Marketing consent
          </Text>
          <Text variant="muted">Send me event drops, route notes, and 6am BKK club updates.</Text>
        </View>
      </Pressable>

      {(validationError ?? error) ? (
        <Text className="text-sm text-destructive" selectable>
          {validationError ?? error}
        </Text>
      ) : null}

      <Button className="h-12" disabled={isSubmitting} onPress={handleSubmit}>
        <Text>{isSubmitting ? 'Saving...' : submitLabel}</Text>
      </Button>
    </View>
  );
}
